import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';

import DefaultPicture, {
  propTypes as defaultPicturePropTypes,
} from '../../../01_atoms/DefaultPicture';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';
import Slider from '../../../01_atoms/Slider';
import SliderNavigationIcon from '../../../../public/static/icons/arrows/carousel-button-outline.svg';
import { propTypes as linkPropTypes } from '../../../01_atoms/Link';
import BlockFooter from '../../../02_molecules/BlockFooter';
import BulletPointBackgroundIcon from '../../../../public/static/icons/bullet-point-background.svg';
import { pushSliderHasChanged as gtmPushSliderHasChanged } from '../../../../utils/gtm';

import styles from './index.module.scss';
import useScreenSize from '../../../../hooks/useScreenSize';

const CardItem = ({ image, title, subtitle = '' }) => (
  <div className={`${styles['card']}`}>
    <div className={`${styles['card__image']}`}>
      <DefaultPicture {...image} />
    </div>
    <div className={`${styles['card__bottom']}`}>
      <h3 className={`${styles['card__title']}`}>{title}</h3>
      {subtitle && <div className={`${styles['card__subtitle']}`}>{subtitle}</div>}
    </div>
  </div>
);

const cardItemPropTypes = {
  image: PropTypes.shape(defaultPicturePropTypes).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

CardItem.propTypes = cardItemPropTypes;

const defaultSliderSettings = {
  dots: false,
  infinite: false,
  lazyLoad: false,
  slidesToScroll: 1,
  arrows: false,
  initialSlide: 0,
  variableWidth: true,
};

const BBCardCarousel = ({
  heading = '',
  items,
  buttonDescription = '',
  button = null,
  behaviorSettings = null,
  uuid = null,
}) => {
  const [slideIndex, setSlideIndex] = useState(1);
  const { width } = useScreenSize();
  const isDesktop = width >= 1200;

  const sliderRef = useRef(null);
  const onNextClick = () => sliderRef.current && sliderRef.current.slickNext();
  const onPrevClick = () => sliderRef.current && sliderRef.current.slickPrev();

  const sliderSettings = defaultSliderSettings;

  const countItems = items.length;
  sliderSettings.afterChange = (index) => {
    const newIndex = Math.round(index + 1);
    setSlideIndex(newIndex);
    gtmPushSliderHasChanged({
      uuid,
      type: 'card_carousel',
      current_item: newIndex,
    });
  };

  let displayArrows = true;

  if (countItems < 5 && isDesktop) {
    displayArrows = false;
  }

  sliderSettings.draggable = displayArrows;
  sliderSettings.swipe = displayArrows;

  const classes = [
    'bb',
    styles['bb-card-carousel'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div id={uuid} className={classes.join(' ')}>
      <div className={styles['bb-card-carousel__block-wrapper']}>
        <div className="container">
          <div className={styles['bb-card-carousel__slider-wrapper']}>
            {heading && <h2 className={`${styles['bb-card-carousel__title']}`}>{heading}</h2>}

            <div
              className={`${styles['slider']} ${!heading && displayArrows ? styles['slider__padding-for-arrows'] : ''}`}
            >
              <Slider {...sliderSettings} ref={sliderRef}>
                {items.map((item, index) => (
                  <div
                    key={item.title}
                    className={styles['bb-card-carousel__slide']}
                    style={{ width: 304 }}
                  >
                    <CardItem title={item.title} image={item.image} subtitle={item.subtitle} />
                    <div className={styles['bb-card-carousel__slide-number']}>
                      <BulletPointBackgroundIcon
                        className={styles['bb-card-carousel__slide-number-background']}
                      />
                      <div className={styles['bb-card-carousel__slide-number-number']}>
                        {index + 1}
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>

            {displayArrows && (
              <div className={styles['slider__arrows']}>
                <SliderNavigationIcon
                  className={`${styles['slider__arrow']} ${styles['slider__arrow--prev']} ${
                    slideIndex === 1 ? styles['slider__arrow--inactive'] : ''
                  }`}
                  onClick={onPrevClick}
                  onKeyPress={onPrevClick}
                  tabIndex="0"
                />
                <div className={styles['slider__info']}>
                  {slideIndex}/{countItems}
                </div>
                <SliderNavigationIcon
                  className={`${styles['slider__arrow']} ${styles['slider__arrow--next']} ${
                    slideIndex === countItems ? styles['slider__arrow--inactive'] : ''
                  }`}
                  onClick={onNextClick}
                  onKeyPress={onNextClick}
                  tabIndex="0"
                />
              </div>
            )}
          </div>

          {button && button.nextLink && !isDesktop && (
            <div className={styles['bb-card-carousel__footer']}>
              <BlockFooter button={button} buttonDescription={buttonDescription} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

BBCardCarousel.propTypes = {
  heading: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.shape(cardItemPropTypes)).isRequired,
  buttonDescription: PropTypes.string,
  button: PropTypes.shape({
    label: PropTypes.string.isRequired,
    nextLink: PropTypes.shape(linkPropTypes),
  }),
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBCardCarousel;
